import { useRef, useCallback } from 'react';
import { triggerHaptic, HapticStyle } from '@/hooks/useHaptics';

interface UseLongPressOptions {
  /** Delay in ms before long press triggers (default: 500) */
  delay?: number;
  /** Movement threshold in pixels to cancel long press during scroll (default: 10) */
  moveThreshold?: number;
  /** Haptic feedback style when triggered (default: 'medium') */
  hapticStyle?: HapticStyle;
  /** Callback when long press is triggered */
  onLongPress: () => void;
  /** Optional callback for regular tap/click */
  onTap?: () => void;
}

interface LongPressHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
}

/**
 * Reusable long-press hook for mobile touch interactions.
 * 
 * Features:
 * - Configurable delay and movement threshold
 * - Cancels on scroll/drag to prevent accidental triggers
 * - Haptic feedback on trigger
 * - Supports both touch and mouse events
 * 
 * @example
 * const longPressHandlers = useLongPress({
 *   onLongPress: () => setShowMenu(true),
 *   onTap: () => navigate('/item'),
 * });
 * 
 * return <div {...longPressHandlers}>Press me</div>;
 */
export function useLongPress({
  delay = 500,
  moveThreshold = 10,
  hapticStyle = 'medium',
  onLongPress,
  onTap,
}: UseLongPressOptions): LongPressHandlers {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const isLongPressTriggered = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    isLongPressTriggered.current = false;
    timerRef.current = setTimeout(() => {
      isLongPressTriggered.current = true;
      triggerHaptic(hapticStyle);
      onLongPress();
    }, delay);
  }, [delay, hapticStyle, onLongPress]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    startTimer();
  }, [startTimer]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartPos.current || !timerRef.current) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

    // Cancel if finger moved beyond threshold (user is scrolling)
    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      clearTimer();
      touchStartPos.current = null;
    }
  }, [moveThreshold, clearTimer]);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    clearTimer();
    
    // If long press was triggered, prevent tap action
    if (isLongPressTriggered.current) {
      e.preventDefault();
      isLongPressTriggered.current = false;
      touchStartPos.current = null;
      return;
    }

    // If we have a tap handler and long press wasn't triggered, call it
    if (onTap && touchStartPos.current) {
      onTap();
    }
    
    touchStartPos.current = null;
  }, [clearTimer, onTap]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    // Only handle left click
    if (e.button !== 0) return;
    startTimer();
  }, [startTimer]);

  const onMouseUp = useCallback(() => {
    if (!isLongPressTriggered.current && onTap) {
      onTap();
    }
    clearTimer();
    isLongPressTriggered.current = false;
  }, [clearTimer, onTap]);

  const onMouseLeave = useCallback(() => {
    clearTimer();
    isLongPressTriggered.current = false;
  }, [clearTimer]);

  return {
    onTouchStart,
    onTouchEnd,
    onTouchMove,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
  };
}
